import { eachWeekendOfInterval, endOfDay, endOfYesterday, subDays } from "date-fns";
import { currentOccupancyDayKey, occupancyDayKey } from "../occupancyDay";
import { nplLotLabelHe } from "./nplLotMapConfig";

const IL_TZ = "Asia/Jerusalem";

function enteringOf(row) {
  return Math.max(0, Number(row?.entering) || 0);
}

function israelHour(value) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IL_TZ,
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  return Number(parts.find((p) => p.type === "hour")?.value || 0);
}

function formatHourRange(date) {
  const h = israelHour(date);
  const next = (h + 1) % 24;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:00 – ${pad(next)}:00`;
}

/**
 * Most recent fully completed Sun–Sat week(s), same rule as Hatachana KPIs.
 */
function getCompletedSunSatWeeks({ lookbackDays = 28, includePrior = false } = {}) {
  const end = endOfYesterday();
  const start = subDays(end, lookbackDays);
  const weekends = eachWeekendOfInterval({ start, end });

  let weekSun;
  let weekSat;
  let priorWeekSun;
  let priorWeekSat;

  for (let i = weekends.length - 1; i >= 0; i--) {
    const d = weekends[i];
    if (!weekSun || !weekSat) {
      if (d.getDay() === 0 && weekSat) {
        weekSun = d;
      } else if (d.getDay() === 6) {
        weekSat = endOfDay(d);
      }
    } else if (includePrior && (!priorWeekSun || !priorWeekSat)) {
      if (d.getDay() === 0 && priorWeekSat) {
        priorWeekSun = d;
      } else if (d.getDay() === 6) {
        priorWeekSat = endOfDay(d);
      }
    } else {
      break;
    }
  }

  return { weekSun, weekSat, priorWeekSun, priorWeekSat };
}

function sumEnteringInRange(hourlyByLocation, lotKeys, from, to) {
  if (!from || !to) return 0;
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  let total = 0;
  lotKeys.forEach((lot) => {
    (hourlyByLocation[lot] || []).forEach((row) => {
      const t = new Date(row.date).getTime();
      if (t >= fromMs && t <= toMs) total += enteringOf(row);
    });
  });
  return total;
}

/**
 * KPI numbers from npl_hourly_stats (already vehicles + gateCross).
 */
export function computeNplKpis(hourlyByLocation = {}, lotKeys = []) {
  const todayKey = currentOccupancyDayKey();
  const last24hMs = Date.now() - 24 * 60 * 60 * 1000;

  let todayVisitors = 0;
  const byLotToday = {};
  const byHour24h = new Map();

  lotKeys.forEach((lot) => {
    byLotToday[lot] = 0;
    (hourlyByLocation[lot] || []).forEach((row) => {
      const entering = enteringOf(row);
      if (!entering) return;
      const ts = new Date(row.date).getTime();
      if (occupancyDayKey(row.date) === todayKey) {
        todayVisitors += entering;
        byLotToday[lot] += entering;
      }
      if (ts >= last24hMs) {
        const hourKey = new Date(row.date).toISOString();
        const prev = byHour24h.get(hourKey) || { date: row.date, entering: 0 };
        prev.entering += entering;
        byHour24h.set(hourKey, prev);
      }
    });
  });

  let rushHour = { visitors: 0, hourLabel: "--:--", dateLabel: "" };
  byHour24h.forEach((bucket) => {
    if (bucket.entering > rushHour.visitors) {
      rushHour = {
        visitors: bucket.entering,
        hourLabel: formatHourRange(bucket.date),
        dateLabel: new Date(bucket.date).toLocaleDateString("he-IL", {
          timeZone: IL_TZ,
          weekday: "short",
          day: "numeric",
          month: "numeric",
        }),
      };
    }
  });

  let leadingLotKey = "";
  let leadingCount = 0;
  lotKeys.forEach((lot) => {
    if (byLotToday[lot] > leadingCount) {
      leadingCount = byLotToday[lot];
      leadingLotKey = lot;
    }
  });

  const { weekSun, weekSat, priorWeekSun, priorWeekSat } = getCompletedSunSatWeeks({
    lookbackDays: 28,
    includePrior: true,
  });
  const lastWeekVisitors = sumEnteringInRange(hourlyByLocation, lotKeys, weekSun, weekSat);
  const priorWeekVisitors = sumEnteringInRange(
    hourlyByLocation,
    lotKeys,
    priorWeekSun,
    priorWeekSat
  );
  let weeklyTrend = null;
  if (priorWeekVisitors > 0) {
    weeklyTrend = Math.round(((lastWeekVisitors - priorWeekVisitors) / priorWeekVisitors) * 100);
  }

  return {
    todayVisitors,
    rushHour,
    leadingLocation: {
      name: leadingLotKey ? nplLotLabelHe(leadingLotKey) : "—",
      count: leadingCount,
    },
    lastWeekVisitors,
    weeklyTrend,
  };
}
